import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, MapPin, Clock, ShieldCheck, Gauge, Zap } from "lucide-react";
import { SITE } from "@/lib/siteData";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="top" className="relative min-h-[100vh] flex items-center pt-20 lg:pt-24 overflow-hidden" data-testid="hero-section">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/33814736/pexels-photo-33814736.jpeg"
          alt="Atelier moderne"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-blue-950/85" />
        <div className="absolute inset-0 bg-grain" />
      </div>

      {/* Decorative gradient blur */}
      <div className="absolute top-1/3 -right-32 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-6">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-xs font-semibold text-white tracking-wider uppercase">Votre partenaire automobile de confiance</span>
            </div>

            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.05] tracking-tight uppercase">
              SPHERE AUTO <br />
              <span className="text-blue-400">L&apos;UNIVERS</span> DE VOTRE AUTOMOBILE
            </h1>

            <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed">
              Nouveau centre auto à <span className="text-white font-semibold">Loos-en-Gohelle</span>. Mécanique, dépannage, carte grise, pièces auto. Une équipe réactive, consciencieuse et à l&apos;écoute.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur border border-white/20 rounded-full text-sm text-white font-medium">
                <Gauge className="w-4 h-4 text-blue-400" /> Diag OFFERT
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur border border-white/20 rounded-full text-sm text-white font-medium">
                <Zap className="w-4 h-4 text-blue-400" /> Devis 24/48h
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur border border-white/20 rounded-full text-sm text-white font-medium">
                <ShieldCheck className="w-4 h-4 text-blue-400" /> Toutes marques
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollTo("#appointment")}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold px-8 py-6 rounded-lg group transition-all hover:scale-105"
                data-testid="hero-cta-rdv-button"
              >
                Prendre rendez-vous
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollTo("#services")}
                size="lg"
                variant="outline"
                className="bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900 text-base font-semibold px-8 py-6 rounded-lg"
                data-testid="hero-cta-services-button"
              >
                Voir nos services
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
              <a href={SITE.phoneTel} className="flex items-start gap-3 group" data-testid="hero-info-phone">
                <div className="p-2.5 bg-white/10 border border-white/20 rounded-lg group-hover:bg-blue-600 group-hover:border-blue-600 transition-colors">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Téléphone</div>
                  <div className="text-sm font-semibold text-white">{SITE.phoneDisplay}</div>
                </div>
              </a>
              <div className="flex items-start gap-3" data-testid="hero-info-address">
                <div className="p-2.5 bg-white/10 border border-white/20 rounded-lg">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Adresse</div>
                  <div className="text-sm font-semibold text-white leading-tight">{SITE.address.street}<br/>{SITE.address.zip} {SITE.address.city}</div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="hero-info-hours">
                <div className="p-2.5 bg-white/10 border border-white/20 rounded-lg">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Horaires</div>
                  <div className="text-sm font-semibold text-white">{SITE.hours.days}<br/>{SITE.hours.time}</div>
                  <div className="text-[11px] font-bold text-red-400 mt-1 inline-flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-500 rounded-full animate-pulse" />
                    Dépannage 24/7
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side: logo display */}
          <div className="lg:col-span-5 hidden lg:flex flex-col items-center justify-center animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative">
              <div className="absolute -inset-8 bg-blue-500/20 rounded-full blur-3xl" />
              <img
                src={SITE.logo.light}
                alt="Logo Sphère Auto"
                className="relative w-full max-w-md drop-shadow-2xl rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
